import { V2_API, apiGet, apiPost, isLoggedIn } from './api';

// Theme persistence + pet preference (localStorage)

const THEME_KEY = 'tl_v2_theme';
const PET_KEY = 'tl_v2_pet';
const FOLLOW_KEY = 'tl_v2_follows';
const BUDDY_PICKS_KEY = 'tl_v2_buddy_picks';

export function getStoredTheme() {
  try { return localStorage.getItem(THEME_KEY) || 'coral'; } catch { return 'coral'; }
}

export function setStoredTheme(name) {
  try { localStorage.setItem(THEME_KEY, name); } catch {}
  // Notify other tabs / components
  window.dispatchEvent(new CustomEvent('tl-v2-theme-change', { detail: { name } }));
}

export function getStoredPet() {
  try {
    const raw = localStorage.getItem(PET_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function setStoredPet(pet) {
  try { localStorage.setItem(PET_KEY, JSON.stringify(pet)); } catch {}
}

// Follow / favourites — keep guests engaged without forcing auth.
// Storage shape: { [petId]: { name, species, photo, ts } }
export function getStoredFollows() {
  try {
    const raw = localStorage.getItem(FOLLOW_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function isFollowing(petId) {
  if (!petId) return false;
  return Boolean(getStoredFollows()[petId]);
}

function writeStoredFollows(map) {
  try { localStorage.setItem(FOLLOW_KEY, JSON.stringify(map || {})); } catch {}
}

function emitFollowChange(petId, following) {
  window.dispatchEvent(new CustomEvent('tl-v2-follows-change', { detail: { petId, following } }));
}

function normalizeServerFollows(items = []) {
  const map = {};
  items.forEach((item) => {
    const id = item.animal_id || item.id;
    if (!id) return;
    map[id] = {
      name: item.name,
      species: item.species,
      photo: item.photo || item.thumbnail || null,
      location: item.location || null,
      ts: item.updated_at ? new Date(item.updated_at).getTime() : Date.now(),
    };
  });
  return map;
}

export async function refreshServerFollows() {
  if (!isLoggedIn()) return getStoredFollows();
  const data = await apiGet(`${V2_API}/follows`);
  const map = normalizeServerFollows(data.follows || []);
  writeStoredFollows(map);
  window.dispatchEvent(new CustomEvent('tl-v2-follows-refresh', { detail: { follows: map } }));
  return map;
}

export async function syncLocalFollows() {
  if (!isLoggedIn()) return getStoredFollows();
  const localMap = getStoredFollows();
  const follows = Object.entries(localMap).map(([animal_id, item]) => ({
    animal_id,
    name: item.name,
    species: item.species,
    photo: item.photo || null,
  }));
  if (follows.length > 0) {
    await apiPost(`${V2_API}/follows/sync`, { follows });
  }
  return refreshServerFollows();
}

export async function isFollowingRemote(petId) {
  if (!petId) return false;
  if (!isLoggedIn()) return isFollowing(petId);
  const data = await apiGet(`${V2_API}/follows/status/${encodeURIComponent(petId)}`);
  return Boolean(data.following);
}

export async function toggleFollow(pet) {
  if (!pet || !pet.id) return false;
  if (isLoggedIn()) {
    const data = await apiPost(`${V2_API}/follows/toggle`, {
      animal_id: pet.id,
      name: pet.name,
      species: pet.species,
      photo: pet.photo || pet.thumbnail || null,
    });
    const map = getStoredFollows();
    const next = { ...map };
    if (data.following) {
      next[pet.id] = {
        name: data.follow?.name || pet.name,
        species: data.follow?.species || pet.species,
        photo: data.follow?.photo || pet.photo || pet.thumbnail || null,
        location: data.follow?.location || null,
        ts: Date.now(),
      };
    } else {
      delete next[pet.id];
    }
    writeStoredFollows(next);
    emitFollowChange(pet.id, Boolean(data.following));
    window.dispatchEvent(new CustomEvent('tl-v2-follows-refresh', { detail: { follows: next } }));
    return Boolean(data.following);
  }
  const map = getStoredFollows();
  const next = { ...map };
  let nowFollowing;
  if (next[pet.id]) {
    delete next[pet.id];
    nowFollowing = false;
  } else {
    next[pet.id] = {
      name: pet.name, species: pet.species,
      photo: pet.photo || pet.thumbnail || null,
      ts: Date.now(),
    };
    nowFollowing = true;
  }
  writeStoredFollows(next);
  emitFollowChange(pet.id, nowFollowing);
  window.dispatchEvent(new CustomEvent('tl-v2-follows-refresh', { detail: { follows: next } }));
  return nowFollowing;
}

export function getBuddyPicks() {
  try {
    const raw = localStorage.getItem(BUDDY_PICKS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function addBuddyPick(pet) {
  if (!pet?.id) return getBuddyPicks();
  const picks = getBuddyPicks().filter(p => p.id !== pet.id);
  const item = {
    id: pet.id,
    name: pet.name,
    species: pet.species,
    photo: pet.photo || pet.thumbnail || null,
    location: pet.location || null,
    lat: pet.lat,
    lng: pet.lng,
    distance: pet.distance,
    ts: Date.now(),
  };
  const next = [item, ...picks].slice(0, 24);
  try { localStorage.setItem(BUDDY_PICKS_KEY, JSON.stringify(next)); } catch {}
  window.dispatchEvent(new CustomEvent('tl-v2-buddy-picks-change', { detail: { picks: next } }));
  return next;
}

export function clearBuddyPicks() {
  try { localStorage.removeItem(BUDDY_PICKS_KEY); } catch {}
  window.dispatchEvent(new CustomEvent('tl-v2-buddy-picks-change', { detail: { picks: [] } }));
  return [];
}

